import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import AdminPortalLayout from './layouts/AdminPortalLayout';

// Auth Pages
import LoginPage from './pages/LoginPage';
import CreateOrganizationPage from './pages/CreateOrganizationPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AccessRestrictedPage from './pages/AccessRestrictedPage';
import LandingPage from './pages/LandingPage';

// Main Pages
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import SchedulesPage from './pages/SchedulesPage';
import SchedulingScreen from './pages/SchedulingScreen';
import BaselinesPage from './pages/BaselinesPage';
import HistoryPage from './pages/HistoryPage';
import CalendarsPage from './pages/CalendarsPage';
import ResourcesPage from './pages/ResourcesPage';
import ScenariosPage from './pages/ScenariosPage';
import EVMPage from './pages/EVMPage';
import FieldUpdatesPage from './pages/FieldUpdatesPage';
import AIAssistantPage from './pages/AIAssistantPage';
import NotificationsPage from './pages/NotificationsPage';
import ImportExportPage from './pages/ImportExportPage';
import EmailNotificationsPage from './pages/EmailNotificationsPage';
import UserProfilePage from './pages/UserProfilePage';
import ActivityTablePreviewPage from './pages/dev/ActivityTablePreviewPage';

// Admin Portal Pages
import AdminOverviewPage from './pages/admin/AdminOverviewPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminProjectsPage from './pages/admin/AdminProjectsPage';
import AdminOrganizationsPage from './pages/admin/AdminOrganizationsPage';
import AdminActivityLogPage from './pages/admin/AdminActivityLogPage';
// AdminProjectAccessPage removed - project management moved to org-level overview
import AdminSettingsPage from './pages/admin/AdminSettingsPage';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ProjectProvider>
          <BrowserRouter>
            <Routes>
              {/* Auth Routes (public) */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/create-organization" element={<CreateOrganizationPage />} />
                <Route path="/change-password" element={
                  <ProtectedRoute><ChangePasswordPage /></ProtectedRoute>
                } />
              </Route>

              {/* Access Restricted Page */}
              <Route path="/access-restricted" element={
                <ProtectedRoute>
                  <AccessRestrictedPage />
                </ProtectedRoute>
              } />

              {/* Admin Portal (admin/super_admin only) */}
              <Route path="/admin" element={<ProtectedRoute requiredRole="admin" />}>
                <Route element={<AdminPortalLayout />}>
                  <Route index element={<AdminOverviewPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="projects" element={<AdminProjectsPage />} />
                  {/* Project Access removed - integrated into Overview */}
                  <Route path="organizations" element={<AdminOrganizationsPage />} />
                  <Route path="activity-log" element={<AdminActivityLogPage />} />
                  <Route path="settings" element={<AdminSettingsPage />} />
                </Route>
              </Route>

              {/* User App (DashboardLayout, all authenticated users) */}
              <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                {/* Profile */}
                <Route path="/profile" element={
                  <ProtectedRoute><UserProfilePage /></ProtectedRoute>
                } />
                {/* Core */}
                <Route path="/dashboard" element={
                  <ProtectedRoute requiredAccess="dashboard"><DashboardPage /></ProtectedRoute>
                } />
                <Route path="/projects" element={
                  <ProtectedRoute requiredAccess="projects"><ProjectsPage /></ProtectedRoute>
                } />
                <Route path="/projects/:projectId" element={
                  <ProtectedRoute requiredAccess="projects"><ProjectDetailPage /></ProtectedRoute>
                } />
                <Route path="/schedules" element={
                  <ProtectedRoute requiredAccess="schedules"><SchedulesPage /></ProtectedRoute>
                } />

                {/* Schedule Features - Unified Scheduling Screen */}
                <Route path="/scheduling" element={
                  <ProtectedRoute requiredAccess="activities"><SchedulingScreen /></ProtectedRoute>
                } />
                <Route path="/activities" element={
                  <ProtectedRoute requiredAccess="activities"><SchedulingScreen /></ProtectedRoute>
                } />
                <Route path="/wbs" element={
                  <ProtectedRoute requiredAccess="wbs"><SchedulingScreen /></ProtectedRoute>
                } />
                <Route path="/gantt" element={
                  <ProtectedRoute requiredAccess="gantt"><SchedulingScreen /></ProtectedRoute>
                } />
                <Route path="/baselines" element={
                  <ProtectedRoute requiredAccess="baselines"><BaselinesPage /></ProtectedRoute>
                } />
                <Route path="/history" element={
                  <ProtectedRoute requiredAccess="history"><HistoryPage /></ProtectedRoute>
                } />
                <Route path="/calendars" element={
                  <ProtectedRoute requiredAccess="schedules"><CalendarsPage /></ProtectedRoute>
                } />
                <Route path="/resources" element={
                  <ProtectedRoute requiredAccess="schedules"><ResourcesPage /></ProtectedRoute>
                } />
                <Route path="/scenarios" element={
                  <ProtectedRoute requiredAccess="scenarios"><ScenariosPage /></ProtectedRoute>
                } />
                <Route path="/evm" element={
                  <ProtectedRoute requiredAccess="evm"><EVMPage /></ProtectedRoute>
                } />

                {/* Operations */}
                <Route path="/field-updates" element={
                  <ProtectedRoute requiredAccess="field-updates"><FieldUpdatesPage /></ProtectedRoute>
                } />
                <Route path="/ai-assistant" element={
                  <ProtectedRoute requiredAccess="ai-assistant"><AIAssistantPage /></ProtectedRoute>
                } />

                {/* Settings */}
                <Route path="/notifications" element={
                  <ProtectedRoute requiredAccess="notifications"><NotificationsPage /></ProtectedRoute>
                } />
                <Route path="/email-notifications" element={
                  <ProtectedRoute requiredAccess="notifications"><EmailNotificationsPage /></ProtectedRoute>
                } />
                <Route path="/import-export" element={
                  <ProtectedRoute requiredAccess="import-export"><ImportExportPage /></ProtectedRoute>
                } />

                {/* Dev preview — standalone ActivityTable component */}
                <Route path="/dev/activity-table" element={
                  <ProtectedRoute requiredAccess="activities"><ActivityTablePreviewPage /></ProtectedRoute>
                } />
              </Route>

              {/* Public landing page — redirects to dashboard if already logged in */}
              <Route path="/" element={<LandingPage />} />

              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          {/* Bottom-center + 6s: user's eye stays on the schedule table
              during drag interactions, so a top-right toast with the
              default duration was easy to miss. Moved into the peripheral
              vision zone with a longer dwell time for the reorder-failure
              messages where the user needs to actually read the text. */}
          <Toaster position="bottom-center" richColors duration={6000} />
        </ProjectProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
